import { useEffect, useState } from "react";

import {
  TextArea,
  TextField,
} from "@king-ict/eupisi-ui/components";
import { hrDateWithTime } from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";

import { getCommitteeDecision } from "../../api/Request/RequestApi";
import { ContentArea, ToolbarTop, ToolbarEdit, FormCard, RequestHeader } from "../../components";
import { useAuth } from "../../hooks/auth/useAuth";
import { Role } from "../../models";
import { CommitteeTypeEnum } from "../../models/Committee";
import {
  Request, RequestStatus, RequestType,
  CommitteeLanguageDecision, CommitteeEnrollmentDecision,
  CommitteeDecision
} from "../../models/Request";
import { useGetSchoolYearCurrentQuery } from "../../queries/codeBookQuery";
import { committeeDecisionEditUrl, requestAdminDetailsUrl } from "../../routes/routes";
import { RuleUtils, MsgUtils } from "../../utils";

export function CommitteeDecisionDetails() {
  const { committeeDecisionId, requestId, committeeEmployeeId } = useParams<{ committeeDecisionId: string, requestId: string, committeeEmployeeId: string }>();
  const { t } = useTranslation();
  const history = useHistory();
  const auth = useAuth();

  const createDefault = (): Request => {
    return {
      id: 0,
      requestUID: "",
      code: "",
      statusId: RequestStatus.Draft,
      schoolYearId: 0,
      type: RequestType.RegularEnrollment,
      createdByAdmin: true,
      isOutsideEnrollmentArea: false,
      step: 0,
      subStep: 0,
    };
  };
  const [item, setItem] = useState<Request>(createDefault());
  const [croatianTeacher, setCroatianTeacher] = useState<boolean>(false);
  const [committeeDecision, setCommitteeDecision] = useState<CommitteeDecision | undefined>();

  const { data: schoolYear } = useGetSchoolYearCurrentQuery();

  useEffect(() => {
    (async () => {
      if (committeeDecisionId && requestId && committeeEmployeeId) {
        var result = (await getCommitteeDecision(+committeeDecisionId, +requestId, +committeeEmployeeId));
        if (result) {
          setCommitteeDecision(result);
          if (result.request)
            setItem(result.request);
          setCroatianTeacher(RuleUtils.isCroatianTeacherRole(result.committeeEmployee?.committeeRole?.id));
        }
      }
    })();
  }, [committeeDecisionId, requestId, committeeEmployeeId]);

  const handleEditClicked = async () => {
    history.push(committeeDecisionEditUrl(+committeeDecisionId, +requestId, +committeeEmployeeId));
  };
  const goBack = () => {
    history.push(requestAdminDetailsUrl(+requestId));
  };
  const showEditonToolbarTop = () => {
    var committeeType = committeeDecision?.committeeEmployee?.committeeId === item.institutionCommitteeId
                      ? CommitteeTypeEnum.Institution
                      : CommitteeTypeEnum.County;
    //check request status
    if (!RuleUtils.canEditCommitteeDecision(committeeDecision?.committeeEmployee, item, committeeType)) return false;
    //check auth
    var result = item.schoolYearId
      && schoolYear?.id === item.schoolYearId
      && (auth.currentRole?.role === Role.CAS || auth.currentRole?.role === Role.Helpdesk
      || (committeeDecision?.committeeEmployee?.committeeId === item.institutionCommitteeId
        && (auth.currentRole?.role === Role.InstitutionAdministrator
          || auth.currentRole?.role === Role.InstitutionPrincipal
          || (item.institutionCommitteeId
            && auth.getCommitteeMembershipByCommitteeId(item.institutionCommitteeId)?.some(v => v.isPresident || v.committeeEmployeeId === committeeDecision?.committeeEmployeeId))))
      || (committeeDecision?.committeeEmployee?.committeeId === item.countyCommitteeId
        && (auth.currentRole?.role === Role.CountyAdministrator
          || auth.currentRole?.role === Role.CountyLeader
          || (committeeDecision?.committeeEmployee?.committeeId
            && auth.getCommitteeMembershipByCommitteeId(committeeDecision?.committeeEmployee?.committeeId)?.some(v => v.isPresident || v.committeeEmployeeId === committeeDecision?.committeeEmployeeId)))))
    return result;
  }

  return (
    <ContentArea>
      <ToolbarTop
        showBack={"show"}
        showEdit={showEditonToolbarTop() ? "show" : "hide"}
        pageTitle={t("committeeDecision.committeeDecisionPageTitle")}
        onClickBack={goBack}
        onClickEdit={handleEditClicked}
      />
      <RequestHeader
        request={item} />
      <FormCard>
        <Row>
          <Col md={10}>
            <TextArea
              ariaLabel={t("committeeDecision.remark")}
              label={t("committeeDecision.remark")}
              value={committeeDecision?.remark}
              maxLength={1500}
              readonly
              disabled
              rows={10}
            />
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <TextField
              label={t("committeeDecision.committeeEmployee")}
              value={committeeDecision?.committeeEmployee?.employee?.name + ", " + committeeDecision?.committeeEmployee?.committeeRole.name}
              plaintext
            />
          </Col>
          {(committeeDecision?.committeeEmployee?.committeeId !== item.institutionCommitteeId) &&
            <Col md={6}>
              <TextField
                label={t("committeeDecision.committeePosition")}
                value={committeeDecision?.committeeEmployee?.committeePosition?.name}
                plaintext
              />
            </Col>
          }
        </Row>
        <Row>
          <Col md={12}>
            <TextField
              label={t("committeeDecision.committeeEnrollmentDecision")}
              value={MsgUtils.getEnumToCodeBook(CommitteeEnrollmentDecision, "committeeEnrollmentDecision").find(v => v.id === committeeDecision?.committeeEnrollmentDecisionId)?.name}
              plaintext
            />
          </Col>
        </Row>
        {croatianTeacher && (
          <Row>
            <Col md={12}>
              <TextField
                label={t("committeeDecision.committeeLanguageDecision")}
                value={MsgUtils.getEnumToCodeBook(CommitteeLanguageDecision, "committeeLanguageDecision").find(v => v.id === committeeDecision?.committeeLanguageDecisionId)?.name}
                plaintext
              />
            </Col>
          </Row>
        )}
        <Row>
          <Col md={12}>
            <TextField
              label={t("committeeDecision.createdDateTimeUser")}
              value={committeeDecision !== undefined
                ? (hrDateWithTime(committeeDecision!.dateCreated) 
                + (committeeDecision!.createdAppUser != undefined
                    ? ", " + committeeDecision!.createdAppUser?.firstName 
                      + " " + committeeDecision!.createdAppUser?.lastName
                    : ""))
                : ""
              }
              plaintext
            />
          </Col>
        </Row>
        {(committeeDecision && committeeDecision.dateUpdated) &&
        <Row>
          <Col md={12}>
            <TextField
              label={t("committeeDecision.lastChangedDateTimeUser")}
              value={committeeDecision !== undefined
                ? (hrDateWithTime(committeeDecision!.dateUpdated) 
                + (committeeDecision!.appUser != undefined
                    ? ", " + committeeDecision!.appUser?.firstName 
                      + " " + committeeDecision!.appUser?.lastName
                    : ""))
                : ""
              }
              plaintext
            />
          </Col>
        </Row>
        }
      </FormCard>
      <ToolbarEdit
        showCancel={"show"}
        showSave={"hide"}
        onClickCancel={goBack}
      >
      </ToolbarEdit>
    </ContentArea>
  );
}  